import React from 'react';
import { camelCase, assign } from 'lodash';
import gql from 'graphql-tag';
import { SimpleDataTable, base, ColorSpec, EntityField, UiValues, EntityActionType, EntityAction } from '@sdxcorp/sdx-dds-gui-lib';
import apolloClient from '../lib/apolloClient';
import { CalendarDates, SchedulerStatuses } from '../admin/state/types';
import { schedulerRuntimeSchedulesQuery } from '../base/components/molecules/ProcessingDate';
import { CSSObject } from 'styled-components/macro';
import { Box, Text } from 'grommet';
import styled from 'styled-components/macro';
import { colorValue } from '../lib/theme';
import i18next from 'i18next';
import AutoNumeric from 'sdx-autonumeric-lib';
import { adminRouteTo } from './routing';

const bI18nextT = i18next.t.bind(i18next);

const {
  molecules: { MultiActionButton, TextTruncate },
  atoms: {
    Icons: { ErrorIcon, SuccessIcon, ArrowRight, ArrowLeft, Add },
  },
} = base;

const {
  molecules: {
    StatusContainer: { default: StatusContainer },
  },
} = base;

export const statusColors: Record<string, { bg: ColorSpec; border: ColorSpec }> = {
  green: { bg: 'con-success', border: 'con-success-light' },
  blue: { bg: 'six-horizon', border: 'six-horizon-light' },
  yellow: { bg: 'con-warning', border: 'con-warning-light' },
  red: { bg: 'con-error', border: 'con-error-light' },
  grey: { bg: 'con-disabled', border: 'con-separator' },
};

type CustomCellRenderer = (userId: string | undefined) => SimpleDataTable.ColumnDefinition['cellRenderer'];

export const statusFieldRendered =
  (userId: string | undefined): SimpleDataTable.ColumnDefinition['cellRenderer'] =>
  (_, data) => {
    const colorMapping: Record<string, { bg: ColorSpec; border: ColorSpec }> = {
      ACTIVE: statusColors.green,
      INACTIVE: statusColors.yellow,
      SUSPENDED: statusColors.red,
      REQUESTED: statusColors.blue,
      DELETED: statusColors.grey,
      PENDING_COMPLIANCE_CHECK: statusColors.blue,
    };
    return data.value ? (
      <StatusContainer testId="status-field-container" status={data.value.processed as string} colors={colorMapping[data.value.raw as string]} />
    ) : null;
  };

const totalFieldRenderer: SimpleDataTable.ColumnDefinition['cellRenderer'] = (_, data) => {
  return (
    <>
      {data?.value?.raw
        ? AutoNumeric.format(data?.value?.raw as string, {
            decimalPlaces: 2,
            digitGroupSeparator: "'",
          })
        : ''}
    </>
  );
};

const amountFieldRenderer: SimpleDataTable.ColumnDefinition['cellRenderer'] = (_, data) => {
  return (
    <>
      {data?.value?.raw
        ? AutoNumeric.format(data?.value?.raw as string, {
            decimalPlaces: 2,
            digitGroupSeparator: "'",
          })
        : ''}
    </>
  );
};

const instructionStatusFieldRendered: SimpleDataTable.ColumnDefinition['cellRenderer'] = (_, data) => {
  const colorMapping: Record<string, { bg: ColorSpec; border: ColorSpec }> = {
    RECEIVED: statusColors.blue,
    REJECTED: statusColors.red,
    PENDING: statusColors.yellow,
    ACCEPTED: statusColors.blue,
    CANCELLED: statusColors.grey,
    SETTLED: statusColors.green,
  };
  return data.value ? (
    <StatusContainer testId="instruction-status-field" status={data.value.processed as string} colors={colorMapping[data.value.raw as string]} />
  ) : null;
};

const refDataRenderers: Record<string, CustomCellRenderer> = {
  status: statusFieldRendered,
};

const StyledContainer = styled.div`
  border-radius: 7px;
  border: 1px solid #90999a;
  color: #90999a;
  display: inline-block;
  padding: 3px 7px;
`;

const rightAlignStyle: CSSObject = {
  textAlign: 'right',
  display: 'block',
  width: '100%',
  height: 'unset',
};

const rightAlignedText: SimpleDataTable.ColumnDefinition['cellRenderer'] = (props, data) => (
  <TextTruncate testId={`row-${props.rowIndex}-col-${props.columnIndex}`} text={data.value.processed as string} style={rightAlignStyle} />
);
const rightAlignedHeaderText: SimpleDataTable.HeaderCellRenderer = props => (
  <TextTruncate testId={props.column.title.split(' ').join('-')} text={props.column.title as string} style={rightAlignStyle} />
);

export const orderByParameter = 'orderBy';
export const limitParameter = 'limit';
export const extractSortParameter = (filterQuery: string | undefined, orderByQuery: string | undefined) => {
  let orderBy = '';
  //'[[?e :amount ?p0][(>= ?p0 $1)][(get-attr ?e :amount nil) [?ob]]]' -> orderBy = amount
  filterQuery?.split('get-attr ?e :').forEach((part, index) => {
    if (index > 0) {
      orderBy = part.split(' ')[0];
    }
  });
  const orderDirection = orderByQuery?.includes('asc') ? 'ASC' : 'DESC';
  return `${orderBy} ${orderDirection}`;
};

export const customInstructionCellRenderers = (fieldNames?: string[]) => {
  const allRenderers: Record<string, SimpleDataTable.ColumnDefinition['cellRenderer']> = {
    settlementQuantity: rightAlignedText,
    settlementAmount: rightAlignedText,
    processingStatusAdditionalInformation: (_, data) => {
      const colorMapping = (status: string) => {
        if (status.includes('settled')) return statusColors.green;
        else if (status.includes('pending')) return statusColors.yellow;
        else if (status.includes('rejected')) return statusColors.red;
        else if (status.includes('cancelled')) return statusColors.grey;
        else if (status.includes('accepted')) return statusColors.blue;
        else if (status.includes('lack')) return statusColors.grey;
      };
      const status = (data.value.raw as string)?.toLowerCase();
      const colors = colorMapping(status);
      return data.value ? <StatusContainer testId="status-container" status={data.value.processed as string} colors={colors} /> : null;
    },
  };

  if (!fieldNames) {
    return allRenderers;
  }

  return fieldNames.reduce<Record<string, SimpleDataTable.ColumnDefinition['cellRenderer']>>((acc, fName) => {
    return {
      ...acc,
      [fName as keyof typeof allRenderers]: allRenderers[fName as keyof typeof allRenderers],
    };
  }, {});
};

export const customHoldingRenderers = {
  'asset.status': statusFieldRendered(undefined),
};

export const customMemberAccountBalanceReportRenderers = {
  total: totalFieldRenderer,
};

export const customHoldingCashRenderers = {
  total: totalFieldRenderer,
};

export const customMovementsCashRenderers = {
  amount: amountFieldRenderer,
  available: amountFieldRenderer,
};

export const customPaymentInstructionsRenderers = {
  instructionStatus: instructionStatusFieldRendered,
};

const customCellRenderers = {
  asset: refDataRenderers,
  member: refDataRenderers,
  account: refDataRenderers,
  legalEntity: refDataRenderers,
  centralSecuritiesDepository: refDataRenderers,
  whitelistedAddress: refDataRenderers,
  externalEntity: refDataRenderers,
  externalAccount: refDataRenderers,
  eligibilityOnRestrictedAsset: refDataRenderers,
};

export const renderCustomViewButton = (
  entityName: string,
  id: string | undefined,
  push: (route: string) => void,
  label: (value: string) => string,
) => {
  switch (entityName) {
    case 'Account':
      return {
        type: EntityActionType.CLIENT,
        action: 'viewEntityHoldings',
        label: label('referenceData.accounts.actions.viewHoldings'),
        buttonConfig: {
          eventHandler: () => push(adminRouteTo.holdingsCurrent('assets', id)),
        },
      } as EntityAction;
    default:
      return undefined;
  }
};

export const hideDeletedMembersQuery =
  '[?e :nodeRoles ?nr] (not [(some #{:OBSERVER} ?nr)])[?e :status ?p0][(contains? #{:ACTIVE :SUSPENDED :INACTIVE} ?p0)]';

export const getFilterQueryAndArgs = (entityName: string, sdxMemberId: number | undefined) => {
  if (entityName === 'Asset' && sdxMemberId !== undefined) {
    return {
      filterQuery: `
        [?e :issuerAgent ?ia]
        [?ia :sdxMemberId ?smi]
        (not-join [?e ?smi $1 $2 $3 $4]
          [(!= ?smi $1)]
          (or
            (and [?e :private $2][(some? $3)][(some? $4)])
            (and [?e :status  $3][(some? $2)][(some? $4)])
            (and [?e :status  $4][(some? $2)][(some? $3)])
          )
        )`.replace(/\n/g, ' '),
      filterArgs: [sdxMemberId, true, 'INACTIVE', 'DELETED'],
    };
  }

  return {
    filterQuery: undefined,
    filterArgs: undefined,
  };
};

export const getCustomCellRenderer = (entityName: string, fieldName: string, userId: string | undefined) =>
  customCellRenderers[camelCase(entityName) as keyof typeof customCellRenderers]?.[fieldName]?.(userId);

export const customInstructionsHeaderCellRenderers = () => ({
  settlementQuantity: rightAlignedHeaderText,
  settlementAmount: rightAlignedHeaderText,
});

const customHeaderCellRenderers = {};

export const getCustomHeaderCellRenderer = (entityName: string, fieldName: string) =>
  customHeaderCellRenderers[camelCase(entityName) as keyof typeof customHeaderCellRenderers]?.[fieldName];

export const colsWidthWeight = {
  large: {
    Asset: {
      assetId: 0.5,
      status: 0.5,
    },
    Member: {
      sdxMemberId: 0.5,
      status: 0.5,
    },
    Account: {
      status: 0.5,
    },
    LegalEntity: {
      status: 0.5,
    },
    centralSecuritiesDepository: {
      status: 0.5,
    },
  },
  small: {},
};

type MultiActionButtonProps = React.ComponentProps<typeof MultiActionButton>;

const redActionColor = 'red' as MultiActionButtonProps['color'];
const greenActionColor = 'green' as MultiActionButtonProps['color'];

const refDataActions = {
  SuspendEntity: {
    icon: ErrorIcon,
    buttonColor: redActionColor,
  },
  UnsuspendEntity: {
    icon: SuccessIcon,
    buttonColor: greenActionColor,
  },
  ActivateGlobalCertificateAsset: {
    icon: SuccessIcon,
    buttonColor: greenActionColor,
  },
  UnsuspendGlobalCertificateAsset: {
    icon: SuccessIcon,
    buttonColor: greenActionColor,
  },
};

export const customActionBtnConfig = {
  Asset: refDataActions,
  Member: refDataActions,
  Account: refDataActions,
  TaxIncomeCode: {
    TaxCodesDeleteAction: {
      icon: ErrorIcon,
      buttonColor: redActionColor,
    },
  },
  TaxExemptionCode: {
    TaxCodesDeleteAction: {
      icon: ErrorIcon,
      buttonColor: redActionColor,
    },
  },
  TradingParticipant: {
    DeleteTradingParticipant: {
      icon: ErrorIcon,
      buttonColor: redActionColor,
    },
  },
  FractionalisationAsset: {
    DeleteFractionalisationAsset: {
      icon: ErrorIcon,
      buttonColor: redActionColor,
    },
  },
  StandingSettlementInstruction: {
    DeleteStandingSettlementInstruction: {
      icon: ErrorIcon,
      buttonColor: redActionColor,
    },
  },
  SettlementInstruction: {
    CancelSettlementInstruction: {
      icon: ErrorIcon,
      buttonColor: redActionColor,
    },
    ReleaseCsdHold: {
      icon: ArrowRight,
    },
    SetCsdHold: {
      icon: ArrowLeft,
    },
    ReleaseCsdrHold: {
      icon: ArrowRight,
    },
    SetCsdrHold: {
      icon: ArrowLeft,
    },
    CounterInstruction: {
      icon: Add,
    },
  },
  PaymentInstruction: {
    CancelPaymentInstruction: {
      icon: ErrorIcon,
      buttonColor: redActionColor,
    },
  },
  CorporateActionEvent: {
    ConfirmCorporateActionEvent: {
      icon: SuccessIcon,
      buttonColor: greenActionColor,
    },
    WithdrawCorporateActionEvent: {
      icon: ErrorIcon,
      buttonColor: redActionColor,
    },
  },
  MemberCurrencyEligibilityConfig: {
    SuspendEntity: {
      icon: ErrorIcon,
      buttonColor: redActionColor,
    },
    UnsuspendEntity: {
      icon: SuccessIcon,
      buttonColor: greenActionColor,
    },
  },
  ExternalAccount: {
    InactivateEntity: {
      icon: ErrorIcon,
    },
    ActivateEntity: {
      icon: SuccessIcon,
      buttonColor: greenActionColor,
    },
  },
  EligibilityOnRestrictedAsset: {
    DeleteEligibilityOnRestrictedAsset: {
      icon: ErrorIcon,
      buttonColor: redActionColor,
    },
  },
};

export const upsertEntity = gql`
  mutation UpsertEntity($entity: Any!) {
    upsertEntity(entity: $entity)
  }
`;

// double back slash might be issue when running gen types
export const calendarDatesQuery = (startDay: string, endDay: string) => gql`
  query CalendarDates {
    businessCalendarDays(query: "[[?e :date ?p0][(>= ?p0 $1)][(<= ?p0 $2)][?e :calendar ?c][?c :name \\"SDX-CSD\\"]]", args: ["${startDay}", "${endDay}"]) {
      date
      name
      isBusinessDay
    }
  }
`;

export const getCurrentDate = async () => {
  try {
    const { data } = await apolloClient.query<SchedulerStatuses>({
      query: schedulerRuntimeSchedulesQuery,
      fetchPolicy: 'network-only',
    });
    return data.schedulerStatuses?.[0].processingDate;
  } catch (err) {
    return;
  }
};

export const getNonBusinessDays = async (year: string) => {
  const startDay = `${year}-01-01`;
  const endDay = `${year}-12-31`;
  try {
    const { data } = await apolloClient.query<CalendarDates>({
      query: calendarDatesQuery(startDay, endDay),
      fetchPolicy: 'network-only',
    });
    return data.businessCalendarDays?.filter(day => !day.isBusinessDay).map(day => day.date) ?? [];
  } catch (err) {
    return [];
  }
};

const StyledRedText = styled(Text)`
  color: ${({ theme }) => colorValue(theme, c => c['feedback-error-80'])};
`;

const StyledText = styled(Text)`
  color: ${({ theme }) => colorValue(theme, c => c['white'])};
  font-size: 16px;
`;

export const getBelowTitleComponent = (type: string) => {
  const createLegend = (type: string) => {
    const camelCaseType = type.charAt(0).toLowerCase() + type.slice(1);
    return (
      <Box direction="row" margin={{ top: '3px' }}>
        <StyledText>
          <StyledRedText weight="bold">n</StyledRedText> {bI18nextT(`${camelCaseType}.details.legend.secondRow.part1`) as string}
        </StyledText>
        <StyledText weight="bold" margin={{ horizontal: '5px' }}>
          {bI18nextT(`${camelCaseType}.details.legend.secondRow.part2`) as string}
        </StyledText>
        <StyledText>{bI18nextT(`${camelCaseType}.details.legend.secondRow.part3`) as string}</StyledText>
      </Box>
    );
  };
  return (
    <Box margin={{ top: '20px', bottom: '-27px' }}>
      <StyledText>
        <StyledRedText>*</StyledRedText> {bI18nextT('asset.details.legend.firstRow') as string}
      </StyledText>
      {(type === 'Asset' || type === 'Member' || type === 'LegalEntity') && createLegend(type)}
    </Box>
  );
};

export const createRangeParameters = (param: string, part: string) => {
  const rangeParameters: string[] = [];
  if (part.match('>=')) {
    rangeParameters.push(param.concat('Min'));
  }
  if (part.match('<=')) {
    rangeParameters.push(param.concat('Max'));
  }
  return rangeParameters;
};

export const StyledSuperScript = styled.div`
  position: absolute;
  color: ${({ theme }) => colorValue(theme, c => c['feedback-error-80'])};
  display: inline-block;
  font-weight: bold;
  font-size: 9.5px;
  margin: -1.25px 2px 0 1px;
`;

const NonModifiableComp = <StyledSuperScript>n</StyledSuperScript>;

export const getAdditionalAnnotationComponent = (type: string, name: string, values: UiValues) => {
  if (type === 'Asset') {
    if (
      [
        'activationDate',
        'isin',
        'localSecurityId',
        'quantityType',
        'minimumSettlementUnit',
        'settlementUnitMultiple',
        'governingLaw',
        'issueDate',
      ].includes(name)
    ) {
      return NonModifiableComp;
    }
  } else if (type === 'Account') {
    if (values?.status === 'ACTIVE' && ['accountRef', 'iban'].includes(name)) {
      return NonModifiableComp;
    }
  } else if (type === 'Member') {
    if (['legalEntity'].includes(name)) {
      return NonModifiableComp;
    }
  } else if (type === 'TransferRestriction') {
    if (['name'].includes(name)) {
      return NonModifiableComp;
    }
  } else if (type === 'LegalEntity') {
    if (values?.status === 'ACTIVE' && ['openingDate'].includes(name)) {
      return NonModifiableComp;
    }
  }
  return null;
};

const shouldShowReviewBtn = (userId: string | undefined, row: SimpleDataTable.RowType) => {
  if (
    userId &&
    row?.['submitter.identityProviderId'] !== userId &&
    // @ts-ignore
    row?.['__rawGql']?.['type'] !== 'SANCTION_SCREENING' &&
    (row?.['reviewer.identityProviderId'] === 'service-account-compliance-gateway' || !row?.['reviewer.identityProviderId'])
  ) {
    return true;
  }

  return false;
};

export const getActionColRenderer = (
  entityName: string,
  userData?: { userId: string | undefined; sdxMemberId: number | undefined },
): SimpleDataTable.ColumnDefinition | undefined => {
  if (entityName === 'PendingAction') {
    return {
      colName: EntityField.ENTITY_ACTIONS,
      dataKey: EntityField.ENTITY_ACTIONS,
      title: '',
      width: 135,
      attributeType: undefined,
      dataGetter: _ => ({ processed: '', raw: '' }),
      cellRenderer: (_, { row }) => {
        if (shouldShowReviewBtn(userData?.userId, row)) {
          return <StyledContainer>{bI18nextT('pendingAction.approveReject') as string}</StyledContainer>;
        }
        return null;
      },
      isActionCol: true,
      nonSortable: true,
    };
  }
  return undefined;
};

const defaultSettlementInstructionValues = (entityType: string, entitySubType: string | undefined, values: UiValues, prevValues: UiValues) => {
  const result = {};

  // @ts-ignore
  if ('priceType' in values && prevValues?.asset?.[0]?.id !== values?.asset?.[0]?.id) {
    // @ts-ignore
    const assetType = values?.asset?.[0]?.subType;
    if (assetType === 'E' || assetType === 'C') {
      assign(result, { priceType: 'PER_UNIT' });
    } else if (assetType === 'D') {
      assign(result, { priceType: 'PERCENTAGE' });
    }
  }

  return result && Object.keys(result).length === 0 ? undefined : result;
};

export const dynamicValues =
  (entityType: string, entitySubType: string | undefined) =>
  (prevValues: UiValues, values: UiValues): UiValues | undefined => {
    if (entityType === 'SettlementInstruction') {
      return defaultSettlementInstructionValues(entityType, entitySubType, values, prevValues);
    }

    return undefined;
  };

const processingUsagesToExclude = ['CA_PROCESSING', 'ATOMIC_OWN_ACCOUNT_TRADES', 'ATOMIC_CLIENT_TRADES'];

export const filterSelectValues = (id: string, values: string[], selectedValues: string[]) => {
  if (id === 'interestType') {
    return values.filter(value => value === 'FIXED' || value === 'VARIABLE' || selectedValues.includes(value));
  }
  if (id === 'processingUsages') {
    return values.filter(value => !processingUsagesToExclude.includes(value));
  }
  return values;
};
